import React, { useMemo, ReactNode } from "react";
import Avatar from "@mui/material/Avatar";
import MUIListItem, { ListItemProps as MUIListItemProps } from "@mui/material/ListItem";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import classNames from "classnames";

import { ChipList, SmallChip } from "./Chip";
import SVGIcon from "../SVGIcon";

export interface DSListItemProps extends Omit<MUIListItemProps, "content"> {
    extraIcons?: ReactNode[];
    className?: string;
    content?: ReactNode;
    hover?: boolean;
    icon?: ReactNode;
    meta?: ReactNode;
    hoverButton?: ReactNode;
    mode?: string;
    secondary?: ReactNode;
    secondaryHover?: ReactNode;
    selected?: boolean;
    tags?: string[] | undefined;
    tagsRight?: boolean;
    variant?: string;
    darkMode?: boolean;
    revealMetaOnRowHover?: boolean;
    primaryTypographyProps?: object;
    secondaryTypographyProps?: object;
    href?: string;
    listItemButton?: boolean;
    clickable?: boolean;
    fullwidth?: boolean;
    target?: string;
}

const Tags: React.FC<{ tags: string[] }> = ({ tags }) => (
    <ChipList>
        {tags.map((t, k) => (
            <React.Fragment key={k}>{t && <SmallChip key={k} label={t} />}</React.Fragment>
        ))}
    </ChipList>
);

const DSListItem = React.forwardRef<HTMLElement, DSListItemProps>(
    (
        {
            children,
            className = null,
            content = null,
            extraIcons = null,
            hover = false,
            icon = null,
            meta = null,
            hoverButton = null,
            mode = "normal",
            secondary = null,
            secondaryHover = null,
            selected = false,
            tags = [],
            tagsRight = false,
            variant = "none",
            darkMode = false,
            revealMetaOnRowHover = false,
            primaryTypographyProps,
            secondaryTypographyProps,
            listItemButton,
            secondaryAction,
            clickable,
            fullwidth = false,
            ...props
        },
        ref,
    ) => {
        // control layout classes with passed flags
        const classes = useMemo(
            () =>
                classNames(
                    "ws-list-item",
                    {
                        "ws-list-item-dense": mode === "dense",
                        "ws-list-item-single": mode === "normal",
                        "ws-list-item-double": mode === "double",
                        "ws-list-item-triple": mode === "triple",
                        "ws-list-item-variable": mode === "variable",
                        "ws-list-item-long-text": !tags.length || tagsRight, // allows full text space if no tags
                        "ws-list-item-divider": variant === "divided" || variant === "inset",
                        "ws-list-item-inset": variant === "inset",
                        "ws-list-item-hover": hover,
                        "ws-list-item-selected": selected,
                        "ws-list-item-dark": darkMode,
                        "ws-list-item-clickable": clickable,
                        "ws-list-item-fullwidth": fullwidth,
                    },
                    className || undefined,
                ),
            [mode, variant, tags, tagsRight, hover, selected, className, darkMode],
        );

        // use either string or node for icon
        let iconComponent: React.ReactNode = null;
        if (icon) {
            if (typeof icon === "string") {
                iconComponent = <SVGIcon icon={icon} />;
            } else {
                iconComponent = icon;
            }
        }

        const ListComponent: React.ElementType = listItemButton ? ListItemButton : MUIListItem;

        let secondaryValue = secondary;
        if (secondaryHover) {
            secondaryValue = (
                <>
                    <span className="ws-list-item__secondary-nohover">{secondary}</span>
                    <span className="ws-list-item__secondary-hover">{secondaryHover}</span>
                </>
            );
        }

        // display the list item
        return (
            <ListComponent className={classes} {...props} ref={ref}>
                {icon && (
                    <ListItemAvatar>
                        <Avatar>{iconComponent}</Avatar>
                    </ListItemAvatar>
                )}

                {extraIcons && (
                    <span className="ws-list-item__extra-icons">
                        {extraIcons.map((i, idx) => (
                            <span key={idx}>{i}</span>
                        ))}
                    </span>
                )}

                <div className="ws-list-item__primary-content">
                    <ListItemText
                        primary={content}
                        secondary={secondaryValue}
                        primaryTypographyProps={primaryTypographyProps}
                        secondaryTypographyProps={secondaryTypographyProps}
                    />
                    {!!tags.length && !tagsRight && <Tags tags={tags} />}
                </div>
                {secondaryAction && (
                    <ListItemSecondaryAction
                        className={classNames({
                            "ws-list-item__secondary-shift-left": !!meta,
                        })}
                    >
                        {secondaryAction}
                    </ListItemSecondaryAction>
                )}

                <div
                    className={classNames("ws-list-item__secondary-content", {
                        "ws-list-item--revealOnRowHover": revealMetaOnRowHover,
                    })}
                >
                    {!!meta && <span className="ws-list-item__meta">{meta}</span>}
                    {children && <span className="ws-list-item__buttons">{children}</span>}
                    {!!tags.length && tagsRight && <Tags tags={tags} />}
                    {!!hoverButton && (
                        <span className="ws-list-item__hover-button">{hoverButton}</span>
                    )}
                </div>
            </ListComponent>
        );
    },
);

const MemoListItem = React.memo(DSListItem);
export default MemoListItem;

export const DenseListItem = React.forwardRef<HTMLElement, Omit<DSListItemProps, "mode">>(
    (props, ref) => <MemoListItem mode="dense" ref={ref} {...props} />,
);
export const DoubleListItem = React.forwardRef<HTMLElement, Omit<DSListItemProps, "mode">>(
    (props, ref) => <MemoListItem mode="double" ref={ref} {...props} />,
);
export const TripleListItem = React.forwardRef<HTMLElement, Omit<DSListItemProps, "mode">>(
    (props, ref) => <MemoListItem mode="triple" ref={ref} {...props} />,
);
export const VariableListItem = React.forwardRef<HTMLElement, Omit<DSListItemProps, "mode">>(
    (props, ref) => <MemoListItem mode="variable" ref={ref} {...props} />,
);
